import { PlayerRole } from './../models/PlayerRole';
import { Player } from '@/models/Player';
import { ConnectionManager } from './ConnectionManager';
import { GameState, RoomDataStore } from './RoomDataStore';
import { SoundEffectsPlayer } from './SoundEffectsPlayer';

export class RoomCommunicationService {
  constructor(
    private readonly dataStore: RoomDataStore,
    private readonly connectionManager: ConnectionManager,
    private readonly soundEffectsPlayer: SoundEffectsPlayer
  ) {}

  public async connectRoom(roomId: string) {
    this.connectionManager.createConnection(roomId);

    // eslint-disable-next-line
    const on = (methodName: string, method: (...args: any[]) => void) => {
      this.connectionManager.on(methodName, method, this);
    };

    on('onPlayerJoined', this.onPlayerJoined);
    on('onPlayerShareState', this.onPlayerShareState);
    on('onPlayerLeave', this.onPlayerLeave);
    on('onPlayerUpdateDetails', this.onPlayerUpdateDetails);

    on('onPlayerCardSelected', this.onPlayerCardSelected);
    on('onPlayerCardCleared', this.onPlayerCardCleared);
    on('onShowCards', this.onShowCards);
    on('onPlayerShareCard', this.onPlayerShareCard);
    on('onResetGame', this.onResetGame);

    if (!this.connectionManager.isConnected()) {
      this.dataStore.setGameState(GameState.connecting);
      await this.connectionManager.connect();
      this.dataStore.setGameState(GameState.connected);
    }
  }

  public async joinRoom() {
    this.dataStore.setGameState(GameState.joining);
    await this.connectionManager.send('joinRoom', this.dataStore.myDetails?.name);
    this.dataStore.setGameState(GameState.voting);
  }

  public async disconnectRoom() {
    if (this.connectionManager.isConnected()) await this.connectionManager.disconnect();
  }

  public async updatePlayerDetails(details: PlayerDetailsDto) {
    this.connectionManager.send('updatePlayerDetails', details);
  }

  public updatePlayerCardSelected() {
    this.connectionManager.send('updatePlayerCardSelected');
  }
  public updatePlayerCardCleared() {
    this.connectionManager.send('updatePlayerCardCleared');
  }
  public showCards() {
    this.connectionManager.send('showCards');
  }
  public resetGame() {
    this.connectionManager.send('resetGame');
  }

  private onPlayerJoined(connectionId: string, name: string) {
    this.dataStore.addPlayer(new Player(connectionId, name));

    // send your attendance to the user
    this.connectionManager.send('shareState', this.createStateDto());
  }
  private createStateDto(): ShareStateDto {
    const sharePlayCard = this.dataStore.gameState === GameState.results;
    return {
      gameState: this.dataStore.gameState,
      name: this.dataStore.myDetails?.name || '',
      role: this.dataStore.myDetails?.role || PlayerRole.Player,
      hasVoted: !!this.dataStore.selectedCard,
      cardId: (sharePlayCard && this.dataStore.selectedCard?.id) || null
    };
  }
  private onPlayerShareState(connectionId: string, state: ShareStateDto) {
    let player = this.dataStore.getPlayerByConnectionId(connectionId);
    if (!player) {
      player = new Player(connectionId, state.name);
      this.dataStore.addPlayer(player);
    }

    player.updateDetails(state.name, state.role);
    const card = state.cardId && this.dataStore.getCardById(state.cardId);
    if (card) player.setCard(card);
    else player.clear();
    player.setCardSelected(state.hasVoted);
  }
  private onPlayerLeave(connectionId: string) {
    const player = this.dataStore.getPlayerByConnectionId(connectionId);
    if (player) this.dataStore.removePlayer(player);
  }
  private onPlayerUpdateDetails(connectionId: string, details: PlayerDetailsDto) {
    const player = this.dataStore.getPlayerByConnectionId(connectionId);
    player?.updateDetails(details.name, details.role);
  }

  private onPlayerCardSelected(connectionId: string) {
    const player = this.dataStore.getPlayerByConnectionId(connectionId);
    player?.setCardSelected(true);
    this.soundEffectsPlayer.playCardPlaced();
  }
  private onPlayerCardCleared(connectionId: string) {
    const player = this.dataStore.getPlayerByConnectionId(connectionId);
    player?.setCardSelected(false);
  }
  private onShowCards() {
    if (this.dataStore.selectedCard)
      this.connectionManager.send('sharePlayerCard', this.dataStore.selectedCard.id);
    this.dataStore.setGameState(GameState.results);
    this.dataStore.startResetCooldown();
    this.soundEffectsPlayer.playCardFlipped();
  }
  private onPlayerShareCard(connectionId: string, cardId: string) {
    const player = this.dataStore.getPlayerByConnectionId(connectionId);
    const card = this.dataStore.getCardById(cardId);
    if (card) player?.setCard(card);
  }
  private onResetGame() {
    this.dataStore.resetGame();
    this.dataStore.clearResetCooldown();
    this.soundEffectsPlayer.playCardsShuffled();
  }
}

class ShareStateDto {
  constructor(
    public gameState: GameState,
    public name: string,
    public role: PlayerRole,
    public hasVoted: boolean,
    public cardId: string | null
  ) {}
}

class PlayerDetailsDto {
  constructor(public name: string, public role: PlayerRole) {}
}
